import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { KpiCard, PageHeader, StatusChip, BotAvatar, EmptyState } from "@/components/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { Bot, Activity, Users, MessageSquare, AlertTriangle, Webhook, Gauge, Boxes, PlusCircle, ArrowRight, CheckCircle2, UserPlus } from "lucide-react";

const DIST_COLORS = { HEALTHY: "#10b981", DEGRADED: "#f59e0b", ACTION_REQUIRED: "#f43f5e", OFFLINE: "#64748b" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/telegram-accounts").then((r) => setAccounts(r.data.items)).catch(() => setAccounts([]));
  }, []);

  if (!data) return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
      <Skeleton className="h-72 w-full" />
    </div>
  );

  const k = data.kpis;
  const dist = Object.entries(data.status_distribution).map(([name, value]) => ({ name, value })).filter((d) => d.value > 0);

  if (accounts && accounts.length === 0) {
    return (
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl p-8 sm:p-12 text-white" style={{ background: "linear-gradient(150deg,#0b1220 0%,#0f2942 55%,#0077B5 130%)" }} data-testid="connect-hero">
          <div className="max-w-xl space-y-4">
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">Connect your Telegram account</h1>
            <p className="text-slate-300">Manage every Telegram bot you own from one place.</p>
            <div className="grid sm:grid-cols-2 gap-2 pt-1">
              {["Automatically discover your bots", "Create new bots without copying tokens", "Manage all bots from one control plane", "Automatically synchronize future bots"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-200"><CheckCircle2 size={16} className="text-sky-300 shrink-0" /> {t}</div>
              ))}
            </div>
            <Button size="lg" className="mt-3 gap-2" onClick={() => navigate("/accounts/add")} data-testid="btn-hero-connect"><UserPlus size={17} /> Connect Telegram Account</Button>
            <div className="pt-2"><button className="text-xs text-slate-400 underline" onClick={() => navigate("/bots/create")} data-testid="btn-hero-import">or import an existing bot token (advanced)</button></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" desc="Executive view of every bot in this workspace."
        actions={<Button size="sm" onClick={() => navigate("/bots/create")} className="gap-1.5"><PlusCircle size={15} /> New Bot</Button>} testId="dashboard-header" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-total-bots" label="Total Bots" value={k.total_bots} icon={Bot} tone="sky"
          sub={k.hosted_bots != null ? `${k.local_bots} local · ${k.hosted_bots} hosted on BotNexus` : `${k.managed_bots} managed · ${k.imported_bots} imported`} />
        <KpiCard testId="kpi-active-bots-count" label="Active" value={k.active_bots} icon={Activity} tone="emerald" sub={`${k.pending_bots} pending confirmation`} />
        <KpiCard testId="kpi-attention" label="Need Attention" value={k.attention} icon={AlertTriangle} tone="amber" sub="Degraded or action required" />
        <KpiCard testId="kpi-active-users" label="Active Users" value={k.active_users} icon={Users} tone="violet" sub="Across all bots" />
        <KpiCard testId="kpi-messages-today" label="Messages Today" value={k.messages_today} icon={MessageSquare} tone="sky" />
        <KpiCard testId="kpi-updates-today" label="Updates Today" value={k.updates_today} icon={Boxes} tone="emerald" />
        <KpiCard testId="kpi-webhook-health" label="Webhook Health" value={`${k.webhook_healthy}/${k.webhook_healthy + k.webhook_issues}`} icon={Webhook} tone={k.webhook_issues ? "amber" : "emerald"} sub={k.webhook_issues ? `${k.webhook_issues} with errors` : "All healthy"} />
        <KpiCard testId="kpi-api-errors" label="API Errors (24h)" value={k.api_errors} icon={Gauge} tone={k.api_errors ? "rose" : "slate"} sub={`${k.rate_limited} rate-limited`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-card border border-border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Activity · last 7 days</h3>
            <span className="text-xs text-muted-foreground">messages, updates &amp; errors</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.series}>
              <defs>
                <linearGradient id="gMsg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#229ED9" stopOpacity={0.4} /><stop offset="100%" stopColor="#229ED9" stopOpacity={0} /></linearGradient>
                <linearGradient id="gUpd" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="messages" stroke="#229ED9" fill="url(#gMsg)" strokeWidth={2} />
              <Area type="monotone" dataKey="updates" stroke="#10b981" fill="url(#gUpd)" strokeWidth={2} />
              <Area type="monotone" dataKey="errors" stroke="#f43f5e" fill="transparent" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-4 bg-card border border-border rounded-lg p-5">
          <h3 className="font-semibold mb-4">Bot Health</h3>
          {dist.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-10">No bots yet.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={dist} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                    {dist.map((d) => <Cell key={d.name} fill={DIST_COLORS[d.name]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#fff" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {dist.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: DIST_COLORS[d.name] }} />{d.name.replace("_", " ").toLowerCase()}</span>
                    <span className="mono font-medium">{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
            <h3 className="font-semibold">Recent Bots</h3>
            <button className="text-xs text-primary flex items-center gap-1" onClick={() => navigate("/bots")}>View all <ArrowRight size={12} /></button>
          </div>
          {data.recent_bots.length === 0 ? (
            <EmptyState icon={Bot} title="No bots yet" desc="Create a managed bot or import an existing one to get started."
              action={<Button size="sm" onClick={() => navigate("/bots/create")}>Create your first bot</Button>} testId="empty-recent-bots" />
          ) : (
            <div className="divide-y divide-border">
              {data.recent_bots.map((b) => (
                <button key={b.id} data-testid={`recent-bot-${b.id}`} onClick={() => navigate(`/bots/${b.id}`)} className="w-full flex items-center gap-3 px-5 py-3 hover:bg-secondary/50 transition-colors text-left">
                  <BotAvatar name={b.name} username={b.username} size={34} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{b.name}</div>
                    <div className="text-xs text-muted-foreground mono truncate">@{b.username || "unassigned"}</div>
                  </div>
                  <StatusChip status={b.status} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg">
          <div className="px-5 py-3.5 border-b border-border"><h3 className="font-semibold">Recent Activity</h3></div>
          {data.recent_activity.length === 0 ? (
            <EmptyState icon={Activity} title="Nothing yet" desc="Actions across your workspace will appear here." testId="empty-activity" />
          ) : (
            <div className="divide-y divide-border max-h-[320px] overflow-y-auto">
              {data.recent_activity.map((a) => (
                <div key={a.id} className="px-5 py-2.5 text-sm flex items-center justify-between gap-2">
                  <div className="min-w-0"><span className="mono text-xs text-primary">{a.action}</span> <span className="text-muted-foreground text-xs truncate">by {a.user_email}</span></div>
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">{new Date(a.timestamp).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
